import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import './button.css';

export type ButtonVariant = 'primary' | 'ghost' | 'gold' | 'onboard';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Leading icon, e.g. <Icon name="scan" size={20} />. */
  icon?: ReactNode;
}

/**
 * Full-width action. One primary per screen, in the dock. Design rule 5: a disabled
 * primary never sits next to a way out — keep it live and explain what's missing.
 */
export function Button({
  variant = 'primary',
  icon,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={`btn btn--${variant}${className ? ` ${className}` : ''}`} {...rest}>
      {icon}
      {children}
    </button>
  );
}

type TextLinkProps = { quiet?: boolean } & (
  | ({ href: string } & AnchorHTMLAttributes<HTMLAnchorElement>)
  | ({ href?: undefined } & ButtonHTMLAttributes<HTMLButtonElement>)
);

/** Centred text action: the secondary way out ("Remind me later"); `quiet` for tertiary. */
export function TextLink({ quiet, className, ...rest }: TextLinkProps) {
  const cls = `textlink${quiet ? ' textlink--quiet' : ''}${className ? ` ${className}` : ''}`;
  if (rest.href !== undefined)
    return <a className={cls} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)} />;
  const { type = 'button', ...b } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return <button type={type} className={cls} {...b} />;
}

/** Dashed "+ Add …" row (S1 connection, S9 passenger). */
export function AddButton({
  children,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={`addconn${className ? ` ${className}` : ''}`} {...rest}>
      <Icon name="plus" />
      {children}
    </button>
  );
}

/** A link inside running text (S12's "Send it again"). */
export function InlineLink({ className, type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={`inlink${className ? ` ${className}` : ''}`} {...rest} />;
}
